import randomstring from "randomstring";

export const isEmpty = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  } else if (typeof value === "object") {
    return (
      Object.keys(value).length === 0 &&
      (value as Record<string, unknown>).constructor === Object
    );
  } else if (typeof value === "string") {
    return value.length === 0;
  } else if (typeof value === "number") {
    return value === 0;
  } else if (!value) {
    return true;
  }

  return false;
};

export const generateName = (): string => {
  return randomstring.generate({
    length: 10,
    charset: "alphanumeric",
  });
};

export const formatTimestamp = (timestamp: string | number): string => {
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const formatDateTime = (timestamp: string | number): string => {
  try {
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(new Date(timestamp));
  } catch (e) {
    console.error("Error formatting date:", e);
    return "";
  }
};

/** Short display date, e.g. "Aug 17, 2026". */
export const formatDate = (timestamp: string | number): string => {
  try {
    return new Date(timestamp).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (e) {
    return "";
  }
};

/**
 * Compact "how long ago" copy for tables. Invalid or missing input is "—";
 * anything under a minute (or in the future, from clock skew) is "just now";
 * older than 30 days falls back to the short date. `now` is injectable for tests.
 */
export const formatRelativeTime = (
  timestamp: string | number | undefined | null,
  now: number = Date.now()
): string => {
  if (timestamp === undefined || timestamp === null || timestamp === "") return "—";
  const then = typeof timestamp === "number" ? timestamp : Date.parse(timestamp);
  if (Number.isNaN(then)) return "—";

  const seconds = (now - then) / 1000;
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  if (hours < 48) return "yesterday";
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} days ago`;
  return formatDate(then);
};

/**
 * First non-empty line of a (markdown) text, trimmed, with leading markdown
 * markers (#, >, *, -) stripped. "" for empty or missing text.
 */
export const firstLine = (text?: string | null): string => {
  if (!text) return "";
  const line = text.split(/\r?\n/).find((l) => l.trim() !== "");
  if (!line) return "";
  return line.replace(/^[#>*\-\s]+/, "").trim();
};

/**
 * Build file size for a table cell. The API sends bytes, or null / nothing for
 * builds whose size could not be read (older builds, S3 read failures).
 */
export const formatFileSize = (bytes?: number | null): string => {
  if (typeof bytes !== "number" || !Number.isFinite(bytes) || bytes < 0) return "-";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

export const createFormData = (
  url: string,
  id: string | number
): FormData => {
  const formData = new FormData();
  formData.append("url", url);
  formData.append("id", String(id));
  return formData;
};

export default {
  isEmpty,
  formatFileSize,
  generateName,
  formatTimestamp,
  formatDateTime,
  formatDate,
  formatRelativeTime,
  firstLine,
  createFormData,
};
